import { useSyncExternalStore } from "react";

// Режим „само преглед" след гратисния период на лиценза (BE
// `app/features/user_sessions/license_read_only.py`, решение от 30.10.2026).
// Потребител с роля „Справки" или „Склад" още влиза, но:
//  • другите модули не се отварят (`AuthorizedRoute`, плочките в „Модули");
//  • бутоните за действия в „Справки" и „Склад" остават видими, но при
//    натискане показват модала „Само преглед" (`blockIfLicenseReadOnly`);
//  • всеки запис, който все пак стигне до облака, BE отказва с 403
//    `license_read_only` — интерсепторът показва същия модал.
// Кога сме в режима казва САМО облакът: хедърът `X-License-Read-Only` на всеки
// отговор на влязъл потребител (датата на изтичане или `0`), отказът
// `license_read_only` и кадърът на станционния WebSocket. Затова режимът
// започва точно в края на гратиса и свършва при подновяване, без нов вход.

/** `detail.code` на отказа — огледало на BE `LICENSE_READ_ONLY_CODE`. */
export const LICENSE_READ_ONLY_CODE = "license_read_only";
/** Хедърът на отговора — огледало на BE `LICENSE_READ_ONLY_HEADER`. */
export const LICENSE_READ_ONLY_HEADER = "x-license-read-only";
/** Стойността на хедъра при пълен достъп. */
const LICENSE_FULL_ACCESS_VALUE = "0";
/** Ролите (модулите), достъпни в режима — огледало на BE `READ_ONLY_ROLES`. */
const READ_ONLY_ROLE_NAMES: readonly string[] = ["reports", "inventory"];

/** Тялото (`detail`) на 403 `license_read_only`. */
export interface LicenseReadOnlyDetail {
  code: typeof LICENSE_READ_ONLY_CODE;
  message: string;
  expires_at: string;
  grace_ends_at: string;
}

export const isLicenseReadOnlyDetail = (
  value: unknown,
): value is LicenseReadOnlyDetail => {
  if (!value || typeof value !== "object") return false;
  const detail = value as Partial<Record<keyof LicenseReadOnlyDetail, unknown>>;
  return (
    detail.code === LICENSE_READ_ONLY_CODE &&
    typeof detail.expires_at === "string" &&
    typeof detail.grace_ends_at === "string"
  );
};

/** Кадърът на станционния WebSocket преди 1008: `{type: "license_read_only", data}`. */
export const licenseReadOnlyFromSocketFrame = (
  frame: unknown,
): LicenseReadOnlyDetail | null => {
  if (!frame || typeof frame !== "object") return null;
  const { type, data } = frame as { type?: unknown; data?: unknown };
  return type === LICENSE_READ_ONLY_CODE && isLicenseReadOnlyDetail(data)
    ? data
    : null;
};

/** Модул (роля), който се отваря и в режим „само преглед". */
export const isLicenseReadOnlyRole = (roleName: string | undefined) =>
  roleName !== undefined && READ_ONLY_ROLE_NAMES.includes(roleName);

// ─── Състоянието ───────────────────────────────────────────────────────────
// Датата на изтичане (ISO) докато сме в режима, иначе null. Извън Redux, защото
// го пише интерсепторът на axios при всеки отговор, а го четат и обработчици
// на клик без абонамент.

let readOnlyExpiresAt: string | null = null;
const stateListeners = new Set<() => void>();

export const getLicenseReadOnly = (): string | null => readOnlyExpiresAt;

export function setLicenseReadOnly(expiresAt: string | null) {
  if (expiresAt === readOnlyExpiresAt) return;
  readOnlyExpiresAt = expiresAt;
  stateListeners.forEach((listener) => listener());
}

/** Хедърът на облачен отговор: `0` → пълен достъп, дата → режимът, липса → нищо
 * (отговор без влязъл потребител, файл, касата в локален режим). */
export function applyLicenseReadOnlyHeader(value: string | null) {
  if (value === null) return;
  setLicenseReadOnly(value === LICENSE_FULL_ACCESS_VALUE ? null : value);
}

function subscribeLicenseReadOnly(listener: () => void) {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

/** Датата на изтичане, докато сме в режим „само преглед", иначе null. */
export function useLicenseReadOnly(): string | null {
  return useSyncExternalStore(subscribeLicenseReadOnly, getLicenseReadOnly);
}

// ─── Модалът „Само преглед" ────────────────────────────────────────────────
// Показва го `LicenseNoticeHost`; тук е само сигналът, за да го викат и
// интерсепторът, и гейтовете, и бутоните, без да знаят за Redux.

type ReadOnlyNoticeListener = (expiresAt: string) => void;
const noticeListeners = new Set<ReadOnlyNoticeListener>();

export function announceLicenseReadOnly() {
  const expiresAt = readOnlyExpiresAt;
  if (expiresAt === null) return;
  noticeListeners.forEach((listener) => listener(expiresAt));
}

export function onLicenseReadOnlyNotice(
  listener: ReadOnlyNoticeListener,
): () => void {
  noticeListeners.add(listener);
  return () => {
    noticeListeners.delete(listener);
  };
}

/**
 * Пазачът на бутон за действие в „Справки" и „Склад": в режим „само преглед"
 * показва модала и връща true (обработчикът спира), иначе false.
 *
 *   const handleCancel = () => {
 *     if (blockIfLicenseReadOnly()) return;
 *     …
 *   };
 */
export function blockIfLicenseReadOnly(): boolean {
  if (readOnlyExpiresAt === null) return false;
  announceLicenseReadOnly();
  return true;
}
